import clsx from 'clsx';

import { BuyerIntentLeadClick } from 'components/BuyerIntent';
import Card from 'components/Card';
import CtaButton from 'components/CtaButton';
import Icon from 'components/Icon';
import Listing from 'components/Listing';
import Pricing from 'components/Pricing';
import { EventContext, useEventContext } from 'context/EventContext';
import { hasOneTimePayment, hasPeriodicity } from 'helpers/pricing';
import usePplForm from 'hooks/usePplForm';

import Header from './components/Header';
import PricingOptions from './components/PricingOptions';
import RatingComparison from './components/RatingComparison';

import classes from './AlternativeCard.module.scss';

const GetPriceCta = ({ listing }) => {
  const { addEvent } = useEventContext();
  const { buyerIntentProductId, buyerIntentCategoryId, handleClick } =
    usePplForm({
      productId: listing.global_product_id,
      sourceLocation: 'listing-detail-v1/alternatives:get-price',
      formTemplate: 'price',
      includeProductInConfig: true,
    });

  return (
    <BuyerIntentLeadClick
      productId={buyerIntentProductId}
      categoryId={buyerIntentCategoryId}
      sourceLocation="listing-detail-v1/alternatives:get-price"
    >
      <CtaButton
        onClick={handleClick}
        className={classes.cta}
        {...addEvent({
          engagementComponent: 'ppl_banner',
          engagementDestination: 'go-to_ppl-price-form',
          engagementDetails: 'alternatives_get-price',
          name: 'ppl_cta',
        })}
      >
        Get Price
      </CtaButton>
    </BuyerIntentLeadClick>
  );
};

const AlternativeCard = ({
  listing,
  ratingWinners,
  isMain,
  listingPosition,
  showPplPricingBanner,
}) => {
  const showPplCta = showPplPricingBanner && !isMain;
  const hasCta = !isMain || listing.is_premium;
  return (
    <Card className={clsx(classes.root, { [classes.highlight]: isMain })}>
      <EventContext
        value={{ engagementComponent: 'product_alternatives-comparison' }}
      >
        <div className={classes.container}>
          <div
            className={clsx(classes.headerContainer, {
              [classes.noCta]: !hasCta && !showPplCta,
            })}
          >
            <Header
              listing={listing}
              isMain={isMain}
              listingPosition={listingPosition}
            />
          </div>
          {showPplCta ? (
            <div className={classes.ctaContainer}>
              <GetPriceCta listing={listing} />
            </div>
          ) : (
            hasCta && (
              <div className={classes.ctaContainer}>
                <Listing.Buttons.Main
                  listingPosition={listingPosition}
                  listing={listing}
                  enableBuyerIntent={true}
                  className={classes.cta}
                  endIcon={
                    listing.is_premium && (
                      <Icon
                        className={classes.ctaIcon}
                        icon={['far', 'external-link']}
                        size="16"
                      />
                    )
                  }
                />
              </div>
            )
          )}
          <div className={classes.pricingContainer}>
            <div className={classes.fadedOverlay}>
              <div className={classes.content}>
                <div>
                  <Pricing.DisplayLabel
                    pricing={listing.pricing}
                    labelVariant={'small'}
                  />
                  <Pricing.Display
                    pricing={listing.pricing}
                    hidePricingModel={true}
                    className={clsx(
                      !hasCta &&
                        !showPplCta &&
                        !hasPeriodicity(listing.pricing) &&
                        !hasOneTimePayment(listing.pricing) &&
                        classes.noCtaPlaceholder
                    )}
                  />
                  <PricingOptions pricing={listing.pricing} />
                </div>
              </div>
            </div>
          </div>
          <div>
            <RatingComparison listing={listing} ratingWinners={ratingWinners} />
          </div>
        </div>
      </EventContext>
    </Card>
  );
};

export default AlternativeCard;
